import React, { ChangeEvent, useMemo } from 'react';
import { Box, Card, Text, Title } from '@nimbus-ds/components';
import { ExclamationCircleIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { OrderDetailsResponseDto } from '@tiendanube/common';
import { InterfaceNameBooleanValue, InterfaceNameValue } from 'commons/types';
import { getSymbolFromCurrency } from '../../../../../../Auth/hooks/useGetCurrency/useGetCurrency';
import useTranslationOrders from '../../../../../useTranslationOrders';
import { useCanBeRefunded } from '../../../hooks';
import {
  RefundAttributes,
  RefundAttributesTypes,
} from '../../../hooks/useRefundOrder/useRefundOrder';

interface Attributes {
  order: OrderDetailsResponseDto;
  onChange: (data: InterfaceNameValue | InterfaceNameBooleanValue) => void;
  values: RefundAttributes;
}

function RefundAmountSelectorCard({
  order,
  values,
  onChange,
}: Attributes): React.JSX.Element {
  const {
    i18n: { language },
  } = useTranslation();
  const t = useTranslationOrders(
    `refund.setUpRefundDetails.amountSelectorCard`,
  );
  const setType = (type: string) => {
    onChange({ name: 'type', value: type });
  };

  const setValue = (value: string) => {
    onChange({ name: 'value', value: value });
  };

  const { canBePartiallyRefunded } = useCanBeRefunded(order);
  const isPartialRefundEnabled =
    order.payment.supportsPartialRefund && canBePartiallyRefunded();

  const refundableAmount = order.payment.refundableAmount?.value ?? '0.00';

  const updateAmount = (value: string) => {
    if (Number(value) === Number(refundableAmount)) {
      setType(RefundAttributesTypes.TOTAL);
    } else {
      if (values.type === RefundAttributesTypes.TOTAL) {
        onChange({ name: 'returnsStock', value: false });
      }
      setType(RefundAttributesTypes.PARTIAL_BY_VALUE);
    }
    setValue(value);
  };

  const onChangeAmountInput = (event: ChangeEvent<HTMLInputElement>) =>
    updateAmount(event.target.value);

  const hasZeroValueError = useMemo(
    () => values.value !== '' && Number(values.value) <= 0,
    [values],
  );
  const hasHighValueError = useMemo(
    () => Number(values.value) > Number(refundableAmount),
    [refundableAmount, values],
  );
  const hasError = useMemo(
    () => hasZeroValueError || hasHighValueError,
    [hasHighValueError, hasZeroValueError],
  );

  const inputErrorText = useMemo(() => {
    if (hasZeroValueError) {
      return t('errors.mustBeGreaterThanZero');
    }
    if (hasHighValueError) {
      return t('errors.mustBeLowerThanRefundableAmount');
    }
    return undefined;
  }, [hasHighValueError, hasZeroValueError, t]);

  const formatAmount = (amount: string): string =>
    Intl.NumberFormat(language, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));

  return (
    <Card>
      <Title as="h3">{t('title')}</Title>

      <Box display="flex" gap="4" justifyContent="space-between">
        <FormField.Input
          disabled={!isPartialRefundEnabled}
          type="number"
          min="0"
          max={refundableAmount}
          value={isPartialRefundEnabled ? values.value : refundableAmount}
          placeholder={formatAmount('0')}
          onChange={onChangeAmountInput}
          append={
            <Text color="neutral-textLow">
              {getSymbolFromCurrency(order.currency)}
            </Text>
          }
          appendPosition="start"
          appearance={hasError ? 'danger' : 'none'}
          helpText={inputErrorText}
          showHelpText={hasError}
          helpIcon={hasError ? ExclamationCircleIcon : undefined}
          autoFocus
        />
      </Box>

      <Box
        id="divider"
        backgroundColor="neutral-surfaceHighlight"
        height="1px"
      />

      <Text color="neutral-textLow">
        {`${t('description')} ${formatAmount(refundableAmount)}`}
      </Text>
    </Card>
  );
}

export default RefundAmountSelectorCard;
